/** @jsxImportSource theme-ui */
import React from 'react';
import { useSelector } from 'react-redux';
import { SxProp } from 'theme-ui';

import { AggregationCat } from 'models/DiseaseModels';
import { TimeFrameEnumType } from 'models/TimeframeModels';
import { getMomentAgoFromTimeFrameEnumType } from 'utils/dateTimeHelpers';
import {
  MapAggregationCat2AggregationDays,
  MapAggregationCat2TooltipLabel,
} from 'utils/modelHelpers';

import { BdTooltip } from 'components/_controls/BdTooltip';
import { BdSparkGraphBars } from 'components/_graphs/BdSparkGraph/BdSparkGraphBars';

import { SparkTooltipProximal } from 'diseaseActivitiesInLocation/SparkTooltipProximal';
import locationsSelectors from 'locations/locations.selectors';
import riskOverviewSelectors from 'riskOverview/riskOverview.selectors';

const IncidenceSparkAggregationCat: AggregationCat = '3-days';

type AoiVsCountrySparkGraphProps = SxProp & {
  timeFrame: TimeFrameEnumType;
  locationId: number;
  diseaseId: number;
  graphWidth?: number;
};

const AoiVsCountrySparkGraph: React.FC<AoiVsCountrySparkGraphProps> = ({
  timeFrame,
  locationId,
  diseaseId,
  graphWidth = 194,
  ...props
}) => {
  const graphData = useSelector(
    riskOverviewSelectors.getAoiVsCountryCasesGraphData(locationId, diseaseId)
  );

  const locationAoi = useSelector(
    riskOverviewSelectors.getLocationFromNestedCasesTree(diseaseId, locationId)
  );
  const countryAoi = useSelector(
    riskOverviewSelectors.getCountryFromNestedCasesTree(diseaseId, locationId)
  );
  const hasIncidenceCaseData = useSelector(
    riskOverviewSelectors.hasIncidenceCaseData(diseaseId, locationId)
  );

  const locationName = useSelector(locationsSelectors.getLocationName(locationId));

  return (
    <BdTooltip
      fixedWidth="175px"
      className="spark-graph-bars-container"
      customPopup={
        <SparkTooltipProximal
          isInsufficientData={!hasIncidenceCaseData}
          isCountry={locationAoi?.locationName === countryAoi?.locationName}
          timeframeStartDate={getMomentAgoFromTimeFrameEnumType(timeFrame).utc().toDate()}
          aoiName={locationName}
          countryName={countryAoi?.locationName}
          casesInAoi={locationAoi?.caseCounts?.reportedCases}
          casesInCountry={countryAoi?.caseCounts?.reportedCases}
          aggregationDaysStr={MapAggregationCat2TooltipLabel[IncidenceSparkAggregationCat]}
        />
      }
    >
      <div {...props}>
        <BdSparkGraphBars
          graphData={graphData}
          width={graphWidth}
          height={118}
          minDate={getMomentAgoFromTimeFrameEnumType(timeFrame).toDate()}
          aggregationDays={MapAggregationCat2AggregationDays[IncidenceSparkAggregationCat]}
          xAxisTypography="caption"
          xAxisTickDimensions={{ thickness: 4, length: 5 }}
          padding={{
            left: 0,
            right: 0,
            top: 1,
            bottom: 22,
          }}
        />
      </div>
    </BdTooltip>
  );
};

export default AoiVsCountrySparkGraph;
